/**
 * La transition d'une séquence vers la suivante : ce que le bouton « Transitions » de la
 * timeline propose, traduit dans `seg.transition` tel que Remotion le joue (creatorstudio,
 * `Transition` + `computeSegmentTimeline`). Le catalogue est celui du lecteur : le fondu
 * enchaîné (sans couleur, les deux séquences se chevauchent d'une demi-durée), le fondu par
 * une couleur (chaque séquence prend sa moitié), le volet coloré qui arrive d'un côté.
 *
 * Le format garde `type`, `duration`, `color` et `direction` séparés pour que le choix par
 * jointure, et plus tard une durée réglable, n'aient rien à migrer.
 */
export const TRANSITION_KINDS = ["none", "crossfade", "fade_black", "fade_white", "wipe_right", "wipe_left"] as const;
export type TransitionKind = (typeof TRANSITION_KINDS)[number];

export const DEFAULT_TRANSITION_DURATION = 0.5;

export interface SegmentTransition {
  type: "crossfade" | "fade" | "wipe";
  duration: number;
  color?: string;
  /** Le côté d'où le volet arrive : `rtl` depuis la droite, `ltr` depuis la gauche. */
  direction?: "rtl" | "ltr";
}

const BLACK = "#000000";
const WHITE = "#ffffff";

/** La transition d'une sorte, à la durée donnée ; `null` pour aucune. */
export function transitionFor(kind: TransitionKind, duration = DEFAULT_TRANSITION_DURATION): SegmentTransition | null {
  if (!(duration > 0)) return null;
  switch (kind) {
    case "crossfade": return { type: "crossfade", duration };
    case "fade_black": return { type: "fade", duration, color: BLACK };
    case "fade_white": return { type: "fade", duration, color: WHITE };
    case "wipe_right": return { type: "wipe", duration, color: BLACK, direction: "rtl" };
    case "wipe_left": return { type: "wipe", duration, color: BLACK, direction: "ltr" };
    default: return null;
  }
}

/** La sorte qu'une transition décrit — au plus près pour une transition posée autrement
 *  (un template) : un fondu coloré est « noir » sauf s'il est clair, un volet vient de droite
 *  sauf s'il dit `ltr`. */
export function transitionKindOf(transition: Partial<SegmentTransition> | null | undefined): TransitionKind {
  if (!transition || !(transition.duration! > 0)) return "none";
  if (transition.type === "wipe") return transition.direction === "ltr" ? "wipe_left" : "wipe_right";
  if (!transition.color) return "crossfade";
  return isLight(transition.color) ? "fade_white" : "fade_black";
}

const isLight = (color: string): boolean => {
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim())?.[1];
  if (!hex) return /white/i.test(color);
  const full = hex.length === 3 ? hex.replace(/./g, (c) => c + c) : hex;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
  return 0.299 * r + 0.587 * g + 0.114 * b > 128;
};
